import React, { useState, useEffect, useCallback, useContext, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend, BarChart, Bar
} from "recharts";
import {
  makeStyles, Paper, Typography, Grid, CircularProgress,
  TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Divider, Table, TableHead, TableBody, TableRow, TableCell,
  Collapse, Box
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import AssessmentIcon from "@material-ui/icons/Assessment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import TimerIcon from "@material-ui/icons/Timer";
import BlockIcon from "@material-ui/icons/Block";
import ChatIcon from "@material-ui/icons/Chat";
import GetAppIcon from "@material-ui/icons/GetApp";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { asArray } from "../../helpers/asArray";
import { fmtDate } from "../../translate/localeHelper";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  // feat_378: MainContainer recorta con overflowY:hidden + height:100%, así que
  // el scroll vertical tiene que vivir acá (mismo patrón que `mainPaper` en
  // Contacts/Queues). Sin esto los filtros, al pie, quedaban fuera de alcance.
  root: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  banner: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.type === "dark" ? "#1a2744" : "#e8f0fe",
  },
  filters: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
  },
  filterTitle: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    fontWeight: 600,
  },
  card: {
    padding: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    borderRadius: 10,
  },
  cardIcon: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  cardValue: { fontSize: "1.8rem", fontWeight: 700, lineHeight: 1 },
  cardLabel: { color: theme.palette.text.secondary, fontSize: "0.8rem", marginTop: 2 },
  chartPaper: {
    padding: theme.spacing(2),
    borderRadius: 10,
  },
  chartTitle: { fontWeight: 600, marginBottom: theme.spacing(1.5) },
  loader: { display: "flex", justifyContent: "center", padding: theme.spacing(6) },
  sectionLabel: { marginBottom: theme.spacing(1.5), marginTop: theme.spacing(3), fontWeight: 600 },
  queueDot: { width: 10, height: 10, borderRadius: "50%", display: "inline-block", marginRight: 6 },
}));

const PIE_COLORS = ["#667eea", "#f59e0b", "#10b981", "#ef4444", "#8b5cf6", "#ec4899"];

const MetricCard = ({ icon, iconBg, value, label }) => {
  const classes = useStyles();
  return (
    <Paper className={classes.card} elevation={0}>
      <div className={classes.cardIcon} style={{ background: iconBg }}>{icon}</div>
      <div>
        <div className={classes.cardValue}>{value ?? "—"}</div>
        <div className={classes.cardLabel}>{label}</div>
      </div>
    </Paper>
  );
};

const toDateStr = (date) => date.toISOString().split("T")[0];

const escapeCSV = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v).replace(/"/g, '""');
  return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s}"` : s;
};

const exportCSV = (data) => {
  const rows = [];

  rows.push(["=== RESUMEN ===", ""]);
  rows.push(["Total chats", data.total]);
  rows.push(["Cerrados", data.closed]);
  rows.push(["Abiertos", data.open]);
  rows.push(["Pendientes", data.pending]);
  rows.push(["Sin respuesta", data.noResponse ?? ""]);
  rows.push(["Resp. promedio (min)", data.avgResponseMinutes]);
  rows.push(["Mensajes IA", data.aiMessages]);
  rows.push(["Mensajes humano", data.humanMessages]);

  if (data.ticketsDetail?.length) {
    rows.push([]);
    rows.push(["=== DETALLE DE CHATS ===", "", "", "", "", "", "", "", "", ""]);
    rows.push([
      "ID", "Contacto", "Teléfono", "Cola", "Motivo de contacto",
      "Asesor", "Estado", "Fecha creación", "Fecha resolución",
      "Tiempo resolución (min)", "Atendido por IA", "Resumen IA"
    ]);
    data.ticketsDetail.forEach(t => {
      const created = t.createdAt ? new Date(t.createdAt) : null;
      const resolved = t.updatedAt && t.status === "closed" ? new Date(t.updatedAt) : null;
      const resolutionMin = (created && resolved)
        ? Math.round((resolved - created) / 60000)
        : "";
      rows.push([
        t.id,
        t.Contact?.name || t.contact?.name || "",
        t.Contact?.number || t.contact?.number || "",
        t.Queue?.name || t.queue?.name || "",
        t.ContactReason?.name || t.contactReason?.name || "",
        t.User?.name || t.user?.name || "",
        t.status || "",
        created ? created.toLocaleDateString("es-AR") + " " + created.toLocaleTimeString("es-AR") : "",
        resolved ? resolved.toLocaleDateString("es-AR") + " " + resolved.toLocaleTimeString("es-AR") : "",
        resolutionMin,
        (t.respondedByAi ? "Sí" : "No"),
        t.Contact?.aiSummary || t.contact?.aiSummary || ""
      ]);
    });
  }

  const csv = rows.map(r => r.map(escapeCSV).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `dashboard_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Reports = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const today = new Date();
  const thirtyAgo = new Date(); thirtyAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(toDateStr(thirtyAgo));
  const [endDate, setEndDate] = useState(toDateStr(today));
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [selectedUser, setSelectedUser] = useState(String(user?.id ?? ""));
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedReason, setSelectedReason] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = user?.profile === "admin" || user?.profile === "superadmin";
  const [filtersOpen, setFiltersOpen] = useState(isAdmin);

  const selectedUserName = useMemo(() => {
    if (!selectedUser || selectedUser === String(user?.id)) return user?.name || "";
    const found = users.find(u => String(u.id) === String(selectedUser));
    return found ? found.name : user?.name || "";
  }, [selectedUser, users, user]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [usersRes, queuesRes, reasonsRes] = await Promise.all([
          api.get("/users"),
          api.get("/queue"),
          api.get("/contact-reasons")
        ]);
        // ⛔ Acá vivía `usersRes.data?.users || usersRes.data || []`. El
        // segundo término guardaba el OBJETO ENTERO de la respuesta cuando
        // faltaba la clave `users`, y el `users.map()` de más abajo tiraba
        // `TypeError: c.map is not a function` — la pantalla de `/`, o sea
        // WAIA entera para el que recién entra. `GET /users` devuelve
        // `{users, count, hasMore}` (UserController.index) y nunca devolvió un
        // array pelado: ese fallback cubría una forma que el endpoint no tiene.
        setUsers(asArray(usersRes.data?.users, "GET /users → data.users"));
        setQueues(asArray(queuesRes.data, "GET /queue"));
        setReasons(asArray(reasonsRes.data, "GET /contact-reasons"));
      } catch (err) { /* ignore */ }
    };
    fetchMeta();
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      if (selectedUser) params.set("userId", selectedUser);
      if (selectedQueue) params.set("queueId", selectedQueue);
      if (selectedReason) params.set("reasonId", selectedReason);
      const { data: res } = await api.get(`/reports/summary?${params}`);
      setData(res);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [startDate, endDate, selectedUser, selectedQueue, selectedReason]);

  useEffect(() => { fetchReport(); }, []);

  const pieData = data
    ? [
        { name: i18n.t("reports.ia"), value: data.aiMessages || 0 },
        { name: i18n.t("reports.humano"), value: data.humanMessages || 0 }
      ]
    : [];

  const statusPie = data
    ? [
        { name: i18n.t("reports.cerrados"), value: data.closed || 0 },
        { name: i18n.t("reports.abiertos"), value: data.open || 0 },
        { name: i18n.t("reports.pendientes"), value: data.pending || 0 },
      ]
    : [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("dashboard.titulo")}</Title>
      </MainHeader>

      <div className={classes.root}>
        {/* Banner descriptivo */}
        <Paper className={classes.banner} elevation={0}>
          <Typography variant="body2">
            {i18n.t("dashboard.summaryBanner", {
              from: fmtDate(startDate),
              to: fmtDate(endDate),
              userName: selectedUserName
            })}
          </Typography>
        </Paper>

        {loading && !data && (
          <div className={classes.loader}><CircularProgress /></div>
        )}

        {data && (
          <>
            {/* Metric Cards */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<ChatIcon />} iconBg="#667eea" value={data.total} label={i18n.t("reports.totalChats")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<CheckCircleIcon />} iconBg="#10b981" value={data.closed} label={i18n.t("reports.cerrados")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<AssessmentIcon />} iconBg="#3b82f6" value={data.open} label={i18n.t("reports.enAtencion")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<HourglassEmptyIcon />} iconBg="#f59e0b" value={data.pending} label={i18n.t("reports.esperando")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<BlockIcon />} iconBg="#ef4444" value={data.noResponse ?? "—"} label={i18n.t("reports.sinRespuesta")} />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <MetricCard icon={<TimerIcon />} iconBg="#8b5cf6"
                  value={data.avgResponseMinutes != null ? `${data.avgResponseMinutes} min` : "—"}
                  label={i18n.t("reports.respPromedio")}
                />
              </Grid>
            </Grid>

            {/* Charts row */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
              <Grid item xs={12} md={8}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    {i18n.t("reports.chatsPorDia")}
                  </Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={data.daily} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={Math.max(0, Math.floor(data.daily.length / 10) - 1)} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <ChartTooltip formatter={v => [v, i18n.t("reports.chats")]} labelFormatter={l => `${i18n.t("reports.fecha")}: ${l}`} />
                      <Line type="monotone" dataKey="count" stroke="#667eea" dot={false} strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    {i18n.t("reports.estadoChats")}
                  </Typography>
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={statusPie} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {statusPie.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                      </Pie>
                      <Legend />
                      <ChartTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* IA vs Human + By agent + By reason */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper className={classes.chartPaper} elevation={0}>
                  <Typography variant="subtitle1" className={classes.chartTitle}>
                    {i18n.t("reports.mensajesIAvsHumano")}
                  </Typography>
                  {pieData[0].value === 0 && pieData[1].value === 0 ? (
                    <Typography variant="body2" color="textSecondary">{i18n.t("reports.sinDatos")}</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80}
                          dataKey="value" paddingAngle={4}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          labelLine={false}
                        >
                          {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                        </Pie>
                        <Legend />
                        <ChartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Paper>
              </Grid>

              {asArray(data.byUser, "informe → byUser").length > 0 && (
                <Grid item xs={12} md={8}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography variant="subtitle1" className={classes.chartTitle}>
                      {i18n.t("reports.chatsPorAgente")}
                    </Typography>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={asArray(data.byUser, "informe → byUser")} layout="vertical" margin={{ left: 60, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                        <ChartTooltip formatter={v => [v, i18n.t("reports.chats")]} />
                        <Bar dataKey="count" fill="#10b981" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              )}

              {data.byQueue.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography variant="subtitle1" className={classes.chartTitle}>
                      {i18n.t("reports.chatsPorCola")}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t("reports.cola")}</TableCell>
                          <TableCell align="right">{i18n.t("reports.chats")}</TableCell>
                          <TableCell align="right">{i18n.t("reports.pctTotal")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {asArray(data.byQueue, "informe → byQueue").map(q => (
                          <TableRow key={q.queueId}>
                            <TableCell>
                              <span className={classes.queueDot} style={{ backgroundColor: q.color }} />
                              {q.name}
                            </TableCell>
                            <TableCell align="right">{q.count}</TableCell>
                            <TableCell align="right">
                              {data.total > 0 ? `${Math.round(q.count / data.total * 100)}%` : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}

              {data.byReason && data.byReason.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper className={classes.chartPaper} elevation={0}>
                    <Typography variant="subtitle1" className={classes.chartTitle}>
                      {i18n.t("reports.chatsPorMotivo")}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t("reports.motivo")}</TableCell>
                          <TableCell align="right">{i18n.t("reports.chats")}</TableCell>
                          <TableCell align="right">{i18n.t("reports.pctTotal")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {asArray(data.byReason, "informe → byReason").map(r => (
                          <TableRow key={r.reasonId}>
                            <TableCell>{r.name}</TableCell>
                            <TableCell align="right">{r.count}</TableCell>
                            <TableCell align="right">
                              {data.total > 0 ? `${Math.round(r.count / data.total * 100)}%` : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {/* Filtros — debajo de los gráficos */}
        <Box mt={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" className={classes.filterTitle} style={{ marginTop: 0, marginBottom: 0 }}>
              {i18n.t("dashboard.filters.title")}
            </Typography>
            <Button
              size="small"
              onClick={() => setFiltersOpen(o => !o)}
              startIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              {filtersOpen ? i18n.t("dashboard.filters.hide") : i18n.t("dashboard.filters.show")}
            </Button>
          </Box>
          <Collapse in={filtersOpen}>
        <div className={classes.filters}>
          <TextField
            label={i18n.t("reports.desde")}
            type="date"
            size="small"
            variant="outlined"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={i18n.t("reports.hasta")}
            type="date"
            size="small"
            variant="outlined"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel shrink>{i18n.t("reports.agente")}</InputLabel>
            <Select
              value={selectedUser}
              onChange={e => setSelectedUser(e.target.value)}
              label={i18n.t("reports.agente")}
              displayEmpty
            >
              <MenuItem value=""><em>{i18n.t("reports.todos")}</em></MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
            <InputLabel>{i18n.t("reports.cola")}</InputLabel>
            <Select
              value={selectedQueue}
              onChange={e => setSelectedQueue(e.target.value)}
              label={i18n.t("reports.cola")}
            >
              <MenuItem value=""><em>{i18n.t("reports.todas")}</em></MenuItem>
              {queues.map(q => (
                <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
            <InputLabel>{i18n.t("reports.motivoContacto")}</InputLabel>
            <Select
              value={selectedReason}
              onChange={e => setSelectedReason(e.target.value)}
              label={i18n.t("reports.motivoContacto")}
            >
              <MenuItem value=""><em>{i18n.t("reports.todos")}</em></MenuItem>
              {reasons.map(r => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" color="primary" onClick={fetchReport} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : i18n.t("reports.generar")}
          </Button>
          {data && (
            <Button
              variant="outlined"
              startIcon={<GetAppIcon />}
              onClick={() => exportCSV(data)}
            >
              {i18n.t("reports.exportarCSV")}
            </Button>
          )}
        </div>
          </Collapse>
        </Box>
      </div>
    </MainContainer>
  );
};

export default Reports;
